import { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { indiaDate, indiaDateTime, indiaMinute } from "../../include/indiaDate";
import { consoleColors, primaryButtonSx } from "./consoleTheme";

export function resizeLaunchTimes(times, count) {
  const next = (times || []).slice(0, count);
  while (next.length < count) next.push("");
  return next;
}

export function slotWhen(value) {
  const text = String(value || "");
  const date = indiaDate(text);
  const time = text.length >= 16 ? text.slice(11, 16) : "";
  return time ? `${date} ${time}` : date;
}

export function slotReady(slot) {
  if (slot?.launched === "1") return true;
  const at = String(slot?.at || "").slice(0, 16);
  return Boolean(at) && indiaMinute() >= at;
}

export function LaunchTimeFields({ count, times, locked, onCount, onTime }) {
  return (
    <>
      <TextField
        label="How many draws will you launch"
        type="number"
        value={count}
        onChange={(event) => onCount(event.target.value)}
        helperText="From 1 to 30. Each draw gets its own date and time."
      />
      {times.map((time, index) => (
        <TextField
          key={index}
          label={`Draw ${index + 1} date and time`}
          type="datetime-local"
          value={time}
          disabled={Boolean(locked?.[index])}
          onChange={(event) => onTime(index, event.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText={locked?.[index] ? "This draw already has a winner." : "Launch stays closed until this time."}
        />
      ))}
    </>
  );
}

function nameCase(value) {
  return String(value || "").trim().toLowerCase().replace(/(^|\s|-)([a-z])/g, (_, sep, letter) => `${sep}${letter.toUpperCase()}`);
}

function startLaunchAudio() {
  try {
    const previous = window.__cashiLaunchAudio;
    if (previous) {
      previous.pause();
      previous.src = "";
    }
    const audio = new Audio(`${process.env.PUBLIC_URL || ""}/cashi.wav`);
    audio.preload = "auto";
    window.__cashiLaunchAudio = audio;
    audio.play().then(() => {}, () => {});
  } catch (error) {
    window.__cashiLaunchAudio = null;
  }
}

export function LaunchChooser({ open, title, frequencyLabel, launches, loadSlots, single, onClose, onOpen, onOpenFrequency, onShare }) {
  const [tick, setTick] = useState(0);
  const [tab, setTab] = useState("frequency");
  const [slots, setSlots] = useState([]);
  const loadSlotsRef = useRef(loadSlots);
  loadSlotsRef.current = loadSlots;
  useEffect(() => {
    if (!open) return undefined;
    setTab(loadSlotsRef.current && !single ? "frequency" : "times");
    setSlots([]);
    const timer = setInterval(() => setTick((value) => value + 1), 15000);
    return () => clearInterval(timer);
  }, [open, single]);

  const copyLink = async (payload) => {
    if (!onShare) return;
    try {
      const url = await onShare(payload);
      if (!url) return;
      await navigator.clipboard.writeText(url);
      toast.success("Launch link copied");
    } catch {
      toast.info("Could not copy the launch link");
    }
  };

  useEffect(() => {
    if (!open || !loadSlotsRef.current || tab === "times") return undefined;
    let mounted = true;
    loadSlotsRef.current(tab === "all" ? "all" : "frequency").then((result) => {
      if (!mounted) return;
      setSlots(result?.status === "200" ? result.data?.slots || [] : []);
    });
    return () => { mounted = false; };
  }, [open, tab]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 750 }}>{single ? "Launch Lucky Draw Ticket" : "Which draw do you want to launch"}</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14, mb: 1.5 }}>{title}{frequencyLabel ? ` · ${frequencyLabel}` : ""}</Typography>
        {loadSlots && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <Button size="small" variant={tab === "frequency" ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setTab("frequency")}>By frequency</Button>
            <Button size="small" variant={tab === "all" ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setTab("all")}>All</Button>
            <Button size="small" variant={tab === "times" ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setTab("times")}>Set times</Button>
          </Box>
        )}
        {tab !== "times" && loadSlots && (
          <Box sx={{ display: "grid", gap: 1, maxHeight: 360, overflow: "auto", pr: 0.5 }}>
            {slots.length === 0 && (
              <Typography sx={{ color: consoleColors.ink, mb: 1 }}>No issued coupons in this frequency yet.</Typography>
            )}
            {slots.map((slot) => (
              <Box key={slot.key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, border: `1px solid ${consoleColors.line}`, borderRadius: 2, px: 1.5, py: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 750 }}>{slot.label}</Typography>
                  <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{slot.count} issued</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.6, flexShrink: 0 }}>
                  {onShare && (
                    <Button size="small" sx={{ textTransform: "none" }} disabled={!slot.count} onClick={() => copyLink({ result_mode: tab === "all" ? "all" : "frequency", slot_key: slot.key })}>
                      Copy link
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    sx={primaryButtonSx}
                    disabled={!slot.count || !onOpenFrequency}
                    onClick={() => { startLaunchAudio(); onOpenFrequency(tab === "all" ? "all" : "frequency", slot); }}
                  >
                    Launch
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {tab === "times" && (
          <>
            {(!launches || launches.length === 0) && (
              <Typography sx={{ color: consoleColors.ink, mb: 2 }}>{single ? "Set the launch date and time on the edit page." : "Set how many draws and each date and time on the edit page."}</Typography>
            )}
            <Box sx={{ display: "grid", gap: 1 }} data-tick={tick}>
              {(single ? (launches || []).slice(0, 1) : (launches || [])).map((slot) => {
                const ready = slotReady(slot);
                const won = slot.launched === "1";
                return (
                  <Box key={slot.index} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, border: `1px solid ${consoleColors.line}`, borderRadius: 2, px: 1.5, py: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 750 }}>{single ? "Launch" : `Draw ${slot.index + 1}`}</Typography>
                      <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{slotWhen(slot.at)}{ready || won ? "" : " · Not open yet"}</Typography>
                      {!single && (slot.winners || []).map((winner, winnerIndex) => (
                        <Box key={winnerIndex} sx={{ mt: 0.6 }}>
                          <Typography sx={{ fontWeight: 750, color: "#15803d" }}>{nameCase(winner.customer_name) || "Winner"}</Typography>
                          <Typography sx={{ fontSize: 13, color: consoleColors.ink }}>
                            {[winner.customer_mobile, indiaDateTime(winner.winner_at), winner.coupon_code].filter(Boolean).join(" · ")}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.6, flexShrink: 0 }}>
                      {onShare && (single || ready || won) && (
                        <Button size="small" sx={{ textTransform: "none" }} onClick={() => copyLink({ result_mode: "manual", launch_index: single ? 0 : slot.index })}>
                          Copy link
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        sx={primaryButtonSx}
                        disabled={!ready}
                        onClick={() => { startLaunchAudio(); onOpen(slot); }}
                      >
                        Launch
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button sx={{ textTransform: "none" }} onClick={onClose}>Close</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
